import React, { useRef, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import { useConfig } from "../../../utils/config";
import { useAI } from "../utils/useAI";
import { ArtType } from "../Art";
import ToolbarDraw from "../components/ToolbarDraw";
import InputSimple from "../../../ui/components/InputSimple";
import { useArtHistory, HistoryEntry, Object } from "../utils/useArtHistory";
import ArtTopBar from "../components/ArtTopBar";
import { KonvaArtStageRefs } from "../components/KonvaCanvas";
import ArtStage from "../components/ArtStage";
import { useTools } from "../utils/useTools";
import example from "../utils/example.json";
import Button from "../../../ui/components/Button";
import Tooltip from "../../../ui/components/Tooltip";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import GlobalMessage from "../../pricing/GlobalMessage";
import { useLayoutManager } from "../utils/useLayoutManager";
import { useArtName } from "../utils/useArtName";
import { useDrawTool } from "../utils/useDrawTool";
import { useSerializeImage } from "../utils/useSerializeImage";
import { useTransform } from "../utils/useTransform";
import ArtToast, { MessageType } from "../components/ArtToast";

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.background};
    overflow: hidden;
    gap: 10px;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: auto;
    height: auto;
    gap: 10px;
`;

interface ArtEditProps {
    onHomeClick: () => void;
    artId: string;
    name: string;
    isLoading: boolean;
    artType: ArtType;
}

const ArtEdit = ({ onHomeClick, artId, name, isLoading, artType }: ArtEditProps) => {
    const config = useConfig();
    const navigate = useNavigate();
    const { history, currentIndex, setCurrentIndex, addHistoryEntry, updateEntry, clearHistory } = useArtHistory({ artId, artType });
    const stageRef = useRef<KonvaArtStageRefs>(null);
    const { panelSize, aiPanelSize, panelOrientation, reflowStage, toolbarDrawOrientation } = useLayoutManager(stageRef);
    const [objects, setObjects] = useState<Object[]>([]);
    const { selectedTool, selectedColor, selectedThickness, handleToolChange, handleColorChange, handleThicknessChange, tools } = useTools();
    const [description, setDescription] = useState('');
    const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
    const descriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevDescriptionRef = useRef('');
    const [aiImage, setAiImage] = useState('');
    const { artName, handleArtNameChange, updateArtName } = useArtName({ artType, artId, initialName: name, description, hasAIImage: aiImage !== '' });
    const {serializeImage, storeImage, userPng, setUserPng} = useSerializeImage(stageRef, panelSize);
    const [aiToggle, setAiToggle] = useState(true);
    const isInitialHistoryLoadRef = useRef(true);
    const isNavigatingHistoryRef = useRef(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [hasUserChanges, setHasUserChanges] = useState(false);
    const [artToastMessage, setArtToastMessage] = useState<MessageType>(null);
    
    // Create a ref to hold the serializeThenSave function
    const serializeThenSaveRef = useRef<((scenario?: string, objectsProp?: any[]) => void) | null>(null);
    
    // Get transform-related functions and refs first to ensure isTransformingRef is defined
    const { 
        captureTransformations, 
        handleTransformStart,
        handleTransformEnd,
        handleImageAdd,
        isTransformingRef 
    } = useTransform({
        objects,
        setObjects,
        stageRef,
        panelSize,
        imageSize: config.IMAGE_SIZE,
        serializeThenSaveRef
    });

    const serializeThenSave = useCallback((scenario?: string, objectsProp?: any[]) => {
        // Skip if we're in the middle of restoring or transforming
        if (isInitialHistoryLoadRef.current || isNavigatingHistoryRef.current || isTransformingRef.current) {
            // console.log('Skipping serialization during initial load, navigation, or transformation');
            return;
        }
        
        serializeImage().then(imageData => {
            // console.log('serialize ' + scenario);
            storeImage(imageData);
            
            // Get objects with transformation data
            let transformedObjects = captureTransformations();
            if (objectsProp) transformedObjects = objectsProp;
            // console.log('transformedObjects: ', transformedObjects);
            // console.log('transformedObjects length: ', transformedObjects.length);
            
            // Skip if nothing has changed
            const lastEntry = history[history.length - 1];
            if (lastEntry && 
                imageData.pngDataUrl === lastEntry.userPng && 
                description === lastEntry.description) {
                // console.log('No changes detected, skipping history update');
                return;
            }
            
            setHasUserChanges(true);
            // If transformedObjects is empty but we have objects in state, use those instead
            const objectsToSave = transformedObjects.length === 0 && objects.length > 0 
            ? objects.map(obj => ({...obj}))  // Deep copy
            : transformedObjects;
            
            // console.log('Adding to history from serialize with objects length:', objectsToSave.length);
            addHistoryEntry({
                objects: objectsToSave,
                userPng: imageData.pngDataUrl,
                description,
                aiImage,
                ai3dModel: model3D,
            });
        });
    }, [serializeImage, storeImage, objects, addHistoryEntry, description, aiImage, history, isInitialHistoryLoadRef.current, isNavigatingHistoryRef.current, isTransformingRef.current, captureTransformations]);

    // Update the serializeThenSaveRef whenever serializeThenSave changes
    useEffect(() => {
        serializeThenSaveRef.current = serializeThenSave;
    }, [serializeThenSave]);

    const {
        isDrawing,
        currentStroke,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp
    } = useDrawTool({
        stageRef,
        panelSize,
        selectedTool,
        selectedColor,
        selectedThickness,
        tools,
        onPathAdded: (newObject: Object) => {
            setObjects(prev => {
                const updatedObjects = [...prev, newObject];
                serializeThenSave('draw pointer up', updatedObjects);
                return updatedObjects;
            });
        }
    });

    // Initial loading
    useEffect(() => {
        if (isLoading) {
            updateArtName('...');
        } else if (name) {
            // console.log('history', history);
            updateArtName(name);
            setCurrentIndex(history.length === 0 ? -1 : history.length - 1);
            setDescription(history[history.length - 1]?.description || '');
            setObjects(history[history.length - 1]?.objects || []);
            setUserPng(history[history.length - 1]?.userPng || '');
            setAiImage(history[history.length - 1]?.aiImage || '');
            setModel3D(history[history.length - 1]?.ai3dModel || '');
            isInitialHistoryLoadRef.current = false;
        }
    }, [name, isLoading]);

    // useEffect(() => {
    //     console.log('isInitialHistoryLoadRef:', isInitialHistoryLoadRef.current);
    // }, [isInitialHistoryLoadRef.current, history]);

    // Add initial blank history entry
    useEffect(() => {
        // Only add initial history entry if there isn't one already
        if (history.length === 0) {
            // Create a proper initial history entry with empty objects array
            const emptyEntry = {
                objects: [] as Object[],
                userPng: '',
                description: '',
                aiImage: '',
                ai3dModel: '',
            };
            
            // Add the initial entry directly to ensure it's properly created
            // console.log('Creating initial history entry');
            addHistoryEntry(emptyEntry);
        }
    }, [addHistoryEntry, history.length]);

    // Use the AI hook for image generation, background removal, and 3D generation
    const { generateImage, handleRemoveBackground, throttledGenerateImage, handle3DGeneration, model3D, setModel3D, isGenerating3D, stagePhase, setStagePhase, button3dPhase, setButton3dPhase } = useAI({
        config,
        artType,
        panelSize,
        userPng,
        description,
        objects,
        aiImage,
        setAiImage,
        setIsGeneratingImage,
        setArtToastMessage,
        addHistoryEntry,
        updateEntry,
        currentIndex,
        history,
        aiToggle,
        isUpdatingDescription
    });
    
    useEffect(() => {
        if (config.ART_REALTIME && hasUserChanges) {
            if (isInitialHistoryLoadRef.current) {
                // console.log('Initial history load, skipping generate');
                return;
            }

            if (isNavigatingHistoryRef.current) {
                // console.log('Navigating history, skipping generate');
                return;
            }

            throttledGenerateImage();
        }
    }, [hasUserChanges, throttledGenerateImage]);

    const updateDetailedStateFromHistory = async () => {
        // Skip if we're already updating from another source or mid-edit
        if (isInitialHistoryLoadRef.current || isNavigatingHistoryRef.current || isTransformingRef.current || isUpdatingDescription || isDrawing || isGeneratingImage) {
            return;
        }

        // If history exists and we have a valid current index
        if (history.length > 0 && currentIndex >= 0 && currentIndex < history.length) {
            const currentEntry = history[currentIndex];

            // console.log('updating detailed state from history at index:', currentIndex, 'with objects length:', currentEntry.objects.length);
            
            // Update state from history
            await new Promise<void>((resolve) => {
                setObjects(currentEntry.objects);
                setDescription(currentEntry.description);
                setUserPng(currentEntry.userPng);
                setAiImage(currentEntry.aiImage);
                setModel3D(currentEntry.ai3dModel);
                setCurrentIndex(history.length-1);
                resolve();
            });
        }
    };

    // Update objects when history changes
    useEffect(() => {
        if (!config.ART_REALTIME) {
            updateDetailedStateFromHistory();
        }
    }, [history, currentIndex, isInitialHistoryLoadRef, isNavigatingHistoryRef, isUpdatingDescription, isDrawing, isTransformingRef, isGeneratingImage, setObjects, setDescription, setUserPng, setAiImage, setCurrentIndex]);

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        // console.log('Description changed to:', value);
        setIsUpdatingDescription(true);
        setDescription(value);
        
        // Clear any existing timeout
        if (descriptionTimeoutRef.current) {
            clearTimeout(descriptionTimeoutRef.current);
        }
        
        // Set a new timeout - only reset the typing state after user stops typing and update the history entry with the new description
        descriptionTimeoutRef.current = setTimeout(() => {
            // If there's a valid current history entry, update its description
            if (history.length > 0) {
                const thisIndex = currentIndex !== -1 ? currentIndex : history.length - 1;
                const thisEntry = history[thisIndex];
                const prevIndex = thisIndex - 1;
                const prevEntry = history[prevIndex];

                // console.log('thisIndex: ', thisIndex, 'prevIndex: ', prevIndex);
                
                if (thisEntry && prevEntry && prevEntry.aiImage === thisEntry.aiImage) {
                    // Update the description in the history entry
                    // console.log('Updating history entry from description change');
                    updateEntry(thisIndex, {
                        description: value
                    });
                } else {
                    // Create a new history entry
                    // console.log('Creating new history entry from description change');
                    addHistoryEntry({
                        objects,
                        userPng,
                        description: value,
                        aiImage,
                        ai3dModel: model3D
                    });
                }
            }

            setIsUpdatingDescription(false);
        }, 1000); // 1 sec delay after user stops typing
    };

    const onSelectHistoryState = useCallback((entry: HistoryEntry) => {
        // console.log('Select history state:', entry);
        const index = history.findIndex(e => e.iterationId === entry.iterationId);
        if (index !== -1) {
            // Set flags before any state changes to prevent unwanted DB updates
            isNavigatingHistoryRef.current = true;

            // console.log('Restoring history to: ', JSON.stringify(entry.objects));
            
            // Update state from history
            setObjects(entry.objects);
            setDescription(entry.description);
            setUserPng(entry.userPng);
            setAiImage(entry.aiImage);
            setModel3D(entry.ai3dModel);
            setCurrentIndex(index);
            
            // Use setTimeout instead of requestAnimationFrame to ensure all state updates complete
            setTimeout(() => {
                isNavigatingHistoryRef.current = false;
            }, 1000);
        }
    }, [history, setCurrentIndex, setObjects, setDescription, setUserPng, setAiImage]);

    const handleStartOver = useCallback(() => {
        // console.log('Starting over');
        setObjects([]);
        setDescription('');
        setUserPng('');
        setAiImage('');
        setModel3D('');
        stageRef.current?.deselectObjects();

        addHistoryEntry({
            objects: [],
            userPng: '',
            description: '',
            aiImage: '',
            ai3dModel: '',
        });
    }, [addHistoryEntry]);

    const onRemoveBGClick = () => {
        // console.log('Remove BG clicked');
        handleRemoveBackground(aiImage);
    };

    const onGenerateClick = () => {
        throttledGenerateImage(true);
    };

    const onNewProjectClick = async () => {
        // console.log('New project clicked');
        const basePath = artType === '3D' ? '/3D' : '/art';
        await navigate(`${basePath}/new`);
    };

    if (isLoading) {
        return (
            <Root>
                <LoadingSpinner />
            </Root>
        );
    }

    return (
        <Root id="art-edit">
            <ArtToast messageType={artToastMessage} />
            <GlobalMessage config={config} colorMode="light" />
            <ArtTopBar 
                config={config}
                artType={artType}
                artId={artId}
                userPng={userPng} 
                aiImage={aiImage} 
                aiToggleInitial={aiToggle}
                onAIToggleChange={setAiToggle} 
                history={history}
                currentIndex={currentIndex}
                onSelectState={(entry: HistoryEntry) => onSelectHistoryState(entry)}
                onStartOver={handleStartOver}
                onRemoveBGClick={onRemoveBGClick}
                artName={artName}
                handleArtNameChange={handleArtNameChange}
                handleHomeClick={onHomeClick}
                onGenerateClick={onGenerateClick}
                isGeneratingImage={isGeneratingImage}
                onNewProjectClick={onNewProjectClick}
                hasDrawing={userPng !== ''}
                model3D={model3D}
                on3DClick={handle3DGeneration}
                isGenerating3D={isGenerating3D}
                button3dPhase={button3dPhase}
                description={description}
            />
            <Inner id='inner'>
                <ArtStage
                    artType={artType}
                    config={config}
                    description={description}
                    onChange={handleDescriptionChange}
                    onGenerateClick={onGenerateClick}
                    hasDrawing={userPng !== ''}
                    ref={stageRef}
                    imageSize={panelSize}
                    aiPanelSize={aiPanelSize}
                    orientation={panelOrientation}
                    reflowStage={reflowStage}
                    objects={objects}
                    currentStroke={currentStroke}
                    selectedTool={selectedTool}
                    selectedThickness={selectedThickness}
                    selectedColor={selectedColor}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onTransformStart={handleTransformStart}
                    onTransformEnd={handleTransformEnd}
                    onImageAdd={handleImageAdd}
                    onToolChange={handleToolChange}
                    isGeneratingImage={isGeneratingImage}
                    isGenerating3D={isGenerating3D}
                    stagePhase={stagePhase}
                    onSetStagePhase={(phase) => {
                        setStagePhase(phase);
                        setButton3dPhase(phase);
                    }}
                    userPng={userPng}
                    aiImage={aiImage}
                    model3D={model3D}
                />
            </Inner>
            <ToolbarDraw 
                config={config}
                panelSize={panelSize}
                selectedColor={selectedColor}
                selectedThickness={selectedThickness}
                onColorChange={handleColorChange}
                onThicknessChange={handleThicknessChange}
                orientation={toolbarDrawOrientation}
                selectedTool={selectedTool}
                onToolChange={handleToolChange}
            />
        </Root>
    );
};

export default ArtEdit;